import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, Params } from '@angular/router';
import { Location } from '@angular/common';
import { signal, Signal } from '@angular/core';
import { filter, map } from 'rxjs';

/**
 * Interface para información de ruta
 */
export interface RouteInfo {
  url: string;
  params: Params;
  queryParams: Params;
  fragment: string | null;
  title?: string;
  breadcrumbs?: string[];
}

/**
 * Interface para configuración de navegación
 */
export interface NavigationConfig {
  preserveQueryParams?: boolean;
  preserveFragment?: boolean;
  replaceUrl?: boolean;
  skipLocationChange?: boolean;
}

/**
 * Puerto para servicios de autorización de rutas
 */
export interface IRouteAuthPort {
  canNavigate(targetUrl: string): Promise<boolean>;
  getRedirectUrl(deniedUrl: string): string;
}

/**
 * Puerto para servicios de analytics de navegación
 */
export interface IRouteAnalyticsPort {
  trackNavigation(from: string, to: string): void;
  trackPageView(url: string, title?: string): void;
}

/**
 * Helper para gestión de rutas con arquitectura hexagonal
 * Implementa puertos para autorización y analytics
 */
@Injectable({
  providedIn: 'root'
})
export class SuraRouteManagerHelper {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly activatedRoute = inject(ActivatedRoute);

  /**
   * Estado reactivo de la ruta actual
   */
  private currentRouteInfo = signal<RouteInfo>({
    url: '',
    params: {},
    queryParams: {},
    fragment: null
  });

  /**
   * Historia de navegación
   */
  private navigationHistory: string[] = [];

  /**
   * Puerto de autorización inyectable
   */
  private authPort?: IRouteAuthPort;

  /**
   * Puerto de analytics inyectable
   */
  private analyticsPort?: IRouteAnalyticsPort;

  constructor() {
    this.initializeRouteTracking();
  }

  /**
   * Configura el puerto de autorización
   */
  setAuthPort(port: IRouteAuthPort): void {
    this.authPort = port;
  }

  /**
   * Configura el puerto de analytics
   */
  setAnalyticsPort(port: IRouteAnalyticsPort): void {
    this.analyticsPort = port;
  }

  /**
   * Obtiene la información actual de la ruta como Signal
   */
  getCurrentRouteInfo(): Signal<RouteInfo> {
    return this.currentRouteInfo.asReadonly();
  }

  /**
   * Navega a una ruta específica con validación de autorización
   */
  async navigateTo(
    path: string | string[], 
    config: NavigationConfig = {}
  ): Promise<boolean> {
    const targetUrl = Array.isArray(path) ? path.join('/') : path;
    
    // Verificar autorización si hay puerto configurado
    if (this.authPort) {
      const canNavigate = await this.authPort.canNavigate(targetUrl);
      if (!canNavigate) {
        const redirectUrl = this.authPort.getRedirectUrl(targetUrl);
        return this.router.navigate([redirectUrl]);
      }
    }

    // Trackear navegación si hay puerto de analytics
    if (this.analyticsPort) {
      const currentUrl = this.router.url;
      this.analyticsPort.trackNavigation(currentUrl, targetUrl);
    }

    // Realizar la navegación
    const navigationExtras: any = {
      queryParamsHandling: config.preserveQueryParams ? 'preserve' : undefined,
      preserveFragment: config.preserveFragment,
      replaceUrl: config.replaceUrl,
      skipLocationChange: config.skipLocationChange
    };

    return this.router.navigate(Array.isArray(path) ? path : [path], navigationExtras);
  }

  /**
   * Navega con parámetros de consulta específicos
   */
  async navigateWithQuery(
    path: string | string[],
    queryParams: Params,
    config: NavigationConfig = {}
  ): Promise<boolean> {
    const navigationExtras: any = {
      queryParams,
      queryParamsHandling: config.preserveQueryParams ? 'merge' : 'replace',
      preserveFragment: config.preserveFragment,
      replaceUrl: config.replaceUrl,
      skipLocationChange: config.skipLocationChange
    };

    return this.router.navigate(Array.isArray(path) ? path : [path], navigationExtras);
  }

  /**
   * Navega hacia atrás en el historial
   */
  goBack(): void {
    if (this.navigationHistory.length > 1) {
      this.navigationHistory.pop(); // Remover la URL actual
      const previousUrl = this.navigationHistory.pop(); // Obtener la anterior
      if (previousUrl) {
        this.router.navigateByUrl(previousUrl);
      } else {
        this.location.back();
      }
    } else {
      this.location.back();
    }
  }

  /**
   * Navega hacia adelante en el historial
   */
  goForward(): void {
    this.location.forward();
  }

  /**
   * Reemplaza la URL actual sin agregar al historial
   */
  replaceUrl(url: string): void {
    this.location.replaceState(url);
  }

  /**
   * Obtiene el parámetro de una ruta específica
   */
  getRouteParam(paramName: string): string | null {
    return this.activatedRoute.snapshot.paramMap.get(paramName);
  }

  /**
   * Obtiene un parámetro de consulta específico
   */
  getQueryParam(paramName: string): string | null {
    return this.activatedRoute.snapshot.queryParamMap.get(paramName);
  }

  /**
   * Verifica si la ruta actual coincide con un patrón
   */
  isCurrentRoute(routePattern: string): boolean {
    const currentUrl = this.router.url.split('?')[0]; // Ignorar query params
    return currentUrl === routePattern || currentUrl.startsWith(routePattern + '/');
  }

  /**
   * Genera una URL completa basada en una ruta y parámetros
   */
  buildUrl(path: string | string[], queryParams?: Params, fragment?: string): string {
    const urlTree = this.router.createUrlTree(
      Array.isArray(path) ? path : [path], 
      { queryParams, fragment }
    );
    return this.router.serializeUrl(urlTree);
  }

  /**
   * Obtiene el historial de navegación
   */
  getNavigationHistory(): string[] {
    return [...this.navigationHistory];
  }

  /**
   * Limpia el historial de navegación
   */
  clearNavigationHistory(): void {
    this.navigationHistory = [];
  }

  /**
   * Verifica si se puede navegar hacia atrás
   */
  canGoBack(): boolean {
    return this.navigationHistory.length > 1;
  }

  /**
   * Extrae breadcrumbs de la ruta actual
   */
  getBreadcrumbs(): string[] {
    const url = this.router.url.split('?')[0];
    return url.split('/').filter(segment => segment.length > 0);
  }

  /**
   * Inicializa el tracking de rutas
   */
  private initializeRouteTracking(): void {
    // Trackear cambios de ruta
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(event => event as NavigationEnd)
      )
      .subscribe(event => {
        // Actualizar historial
        this.navigationHistory.push(event.url);
        
        // Limitar historial a 50 entradas
        if (this.navigationHistory.length > 50) {
          this.navigationHistory = this.navigationHistory.slice(-50);
        }

        // Actualizar estado reactivo
        this.updateCurrentRouteInfo();

        // Trackear en analytics si está configurado
        if (this.analyticsPort) {
          this.analyticsPort.trackPageView(event.url);
        }
      });

    // Inicializar estado actual
    this.updateCurrentRouteInfo();
  }

  /**
   * Actualiza la información de la ruta actual
   */
  private updateCurrentRouteInfo(): void {
    const routeInfo: RouteInfo = {
      url: this.router.url,
      params: this.activatedRoute.snapshot.params,
      queryParams: this.activatedRoute.snapshot.queryParams,
      fragment: this.activatedRoute.snapshot.fragment,
      breadcrumbs: this.getBreadcrumbs()
    };

    this.currentRouteInfo.set(routeInfo);
  }
}